import React, { useState, useEffect, useRef } from "react";
import {
  CalendarLtr24Regular, CalendarCheckmark24Regular,
  DocumentBulletList24Regular, Settings24Regular, SignOut24Regular,
  ClipboardTask24Regular, CalendarDay24Regular, BookContacts24Regular,
  Person24Regular, Key24Regular, Shield24Regular,
  ChevronRight24Regular,
  PanelLeftContract24Regular, PanelLeftExpand24Regular,
  TaskListSquareLtr24Regular, CalendarClock24Regular,
  CalendarEmpty24Regular, BookOpen24Regular,
  Wrench24Regular, PeopleTeam24Regular, LockClosed24Regular,
  WeatherSunny24Regular, WeatherMoon24Regular,
} from "@fluentui/react-icons";
import { FiDollarSign } from "react-icons/fi";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import { ThemeProvider, useTheme, getBrandColors } from "./ThemeContext";
import SixersLogo             from "./Sixers_Logo.png";
import StudentPerformanceForm from "./StudentPerformanceForm";
import StudentMaster          from "./StudentMaster";
import UserMaster             from "./UserMaster";
import RoleMaster             from "./RoleMaster";
import FieldAuthorization     from "./FieldAuthorization";
import ConfigMaster           from "./ConfigMaster";
import ParentDashboard        from "./ParentDashboard";
import CoachPerformanceView   from "./CoachPerformanceView";
import AttendanceKiosk        from "./AttendanceKiosk";
import StudentRosterReport    from "./StudentRosterReport";
import AttendanceReport       from "./AttendanceReport";
import CoachAttendance        from "./CoachAttendance";
import BatchManagement             from "./BatchManagement";
import KioskCheckIn                from "./KioskCheckIn";
import PerformanceChangeLog        from "./PerformanceChangeLog";
import PromotionReadinessReport    from "./PromotionReadinessReport";
import CoachComplianceReport       from "./CoachComplianceReport";
import CategoryPerformanceReport   from "./CategoryPerformanceReport";
import ExecutiveDashboard          from "./ExecutiveDashboard";
import FinancialDashboard          from "./FinancialDashboard";
import BillingHub                  from "./BillingHub";
import CoachEvaluation           from "./CoachEvaluation";
import DynamicReport             from "./DynamicReport";
import OTOBooking                from "./OTOBooking";
import OTOCoachView              from "./OTOCoachView";
import OTOAdminDashboard         from "./OTOAdminDashboard";
import OTOFinancials             from "./OTOFinancials";
import OTOAvailability           from "./OTOAvailability";
import BatchBulkUpload           from "./BatchBulkUpload";
import BatchScheduleReport       from "./BatchScheduleReport";
import CoachPlanner              from "./CoachPlanner";
import CoachScheduleView         from "./CoachScheduleView";
import CoachingProgram           from "./CoachingProgram";
import RegistrationApproval      from "./RegistrationApproval";
import CoachClockIn              from "./CoachClockIn";
import POSRegister                from "./pos/POSRegister";
import POSCatalog                 from "./pos/POSCatalog";
import POSInventory               from "./pos/POSInventory";
import POSOrders                  from "./pos/POSOrders";
import POSCustomers               from "./pos/POSCustomers";
import POSDiscounts               from "./pos/POSDiscounts";
import POSReports                 from "./pos/POSReports";
import CoachPaymentReport        from "./CoachPaymentReport";
import LaneBookingConfig          from "./LaneBookingConfig";
import FreeTrials                 from "./FreeTrials";
import LaneCustomerApproval      from "./LaneCustomerApproval";
import ApprovalQueue             from "./ApprovalQueue";
import LaneBookingPortal         from "./LaneBookingPortal";
import LaneBookingCalendar       from "./LaneBookingCalendar";
import LaneBookingAdmin          from "./LaneBookingAdmin";
import LaneBookingDashboard      from "./LaneBookingDashboard";
import ClientMaster               from "./ClientMaster";
import PerformanceUpload           from "./PerformanceUpload";
import FuturisticBackground        from "./FuturisticBackground";
import TournamentHub                from "./pages/TournamentHub";
import TournamentCostPlanner        from "./pages/TournamentCostPlanner";
import TournamentScheduler          from "./pages/TournamentScheduler";
import TournamentExpense            from "./pages/TournamentExpense";
import TournamentPLReport           from "./pages/TournamentPLReport";
import TournamentAdminApproval      from "./pages/TournamentAdminApproval";
import TournamentStudentTab         from "./pages/TournamentStudentTab";
import bg6                         from "./Background images/6.jpg";

// ─── Outfit font injection ────────────────────────────────────────────────────
const OUTFIT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');`;

// ─── Role-gated menu config ───────────────────────────────────────────────────
const ALL_SECTIONS = [
  {
    label:"Financials", icon:DocumentBulletList24Regular, roles:["Admin","Super Admin"],
    children:[
      { label:"Academy Dashboard",    icon:DocumentBulletList24Regular, roles:["Admin","Super Admin"] },
      { label:"Finance Overview",     icon:DocumentBulletList24Regular, roles:["Admin","Super Admin"] },
      { label:"Coach Payment Report", icon:DocumentBulletList24Regular, roles:["Admin","Super Admin"] },
      { label:"1-on-1 Financials",   icon:DocumentBulletList24Regular, roles:["Admin","Super Admin"] },
    ],
  },
  {
    label:"Bookings", icon:CalendarCheckmark24Regular, roles:["Admin","Super Admin","Staff","Coach","Parent"],
    children:[
      { label:"Lane Booking Portal", icon:CalendarCheckmark24Regular, roles:["Admin","Super Admin","Staff"] },
      { label:"Lane Calendar",       icon:CalendarLtr24Regular,       roles:["Admin","Super Admin","Staff"] },
      { label:"Booking Management",  icon:DocumentBulletList24Regular,roles:["Admin","Super Admin"] },
      { label:"Book a Session",      icon:CalendarCheckmark24Regular, roles:["Admin","Super Admin","Staff"] },
      { label:"My Sessions",         icon:CalendarDay24Regular,       roles:["Admin","Super Admin","Coach"] },
      { label:"My Availability",     icon:CalendarEmpty24Regular,     roles:["Admin","Super Admin","Coach"] },
      { label:"1-on-1 Admin",        icon:DocumentBulletList24Regular,roles:["Admin","Super Admin"] },
      { label:"Free Trials",         icon:CalendarCheckmark24Regular, roles:["Admin","Super Admin","Staff"] },
    ],
  },
  {
    label:"Client Management", icon:PeopleTeam24Regular, roles:["Admin","Super Admin","Coach","Staff"],
    children:[
      { label:"Approval Queue",  icon:ClipboardTask24Regular, roles:["Admin","Super Admin","Staff"] },
      { label:"Client Profile",  icon:BookContacts24Regular,  roles:["Admin","Super Admin","Coach","Staff"] },
      { label:"Billing Hub",     icon:CalendarClock24Regular, roles:["Admin","Super Admin"] },
    ],
  },
  {
    label:"Programs & Batches", icon:CalendarClock24Regular, roles:["Admin","Super Admin","Coach","Staff"],
    children:[
      { label:"Coaching Programs",  icon:ClipboardTask24Regular,     roles:["Admin","Super Admin"] },
      { label:"Batch Management",   icon:CalendarEmpty24Regular,     roles:["Admin","Super Admin","Coach"] },
      { label:"Batch Data Import",  icon:ClipboardTask24Regular,     roles:["Admin","Super Admin"] },
      { label:"Kiosk Check-In",     icon:CalendarCheckmark24Regular, roles:["Admin","Super Admin","Coach","Staff"] },
    ],
  },
  {
    label:"Performance Tracking", icon:TaskListSquareLtr24Regular, roles:["Admin","Super Admin","Coach","Staff"],
    children:[
      { label:"Record Performance",     icon:ClipboardTask24Regular, roles:["Admin","Super Admin","Coach"] },
      { label:"Upload Performance",     icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Coach"] },
      { label:"Coach Attendance",       icon:BookOpen24Regular,      roles:["Admin","Super Admin","Coach","Staff"] },
    ],
  },
  {
    label:"Coach Planning", icon:CalendarClock24Regular, roles:["Admin","Super Admin","Coach"],
    children:[
      { label:"Monthly Planner", icon:CalendarLtr24Regular,        roles:["Admin","Super Admin"] },
      { label:"My Schedule",     icon:CalendarDay24Regular,        roles:["Admin","Super Admin","Coach"] },
      { label:"Clock-In",        icon:CalendarCheckmark24Regular,  roles:["Admin","Super Admin","Coach"] },
    ],
  },
  {
    label:"Tournaments", icon:CalendarCheckmark24Regular, roles:["Admin","Super Admin"],
    children:[
      { label:"Tournament Hub", icon:CalendarCheckmark24Regular, roles:["Admin","Super Admin"] },
    ],
  },
  {
    label:"Reports & Insights", icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Coach","Staff","Parent"],
    children:[
      { label:"Student Roster",         icon:CalendarLtr24Regular,        roles:["Admin","Super Admin","Coach","Staff"] },
      { label:"Attendance Report",      icon:CalendarDay24Regular,        roles:["Admin","Super Admin","Coach","Staff"] },
      { label:"Batch Schedule Report",  icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Coach","Staff"] },
      { label:"Promotion Readiness",    icon:TaskListSquareLtr24Regular,  roles:["Admin","Super Admin","Coach"] },
      { label:"Coach Compliance",       icon:ClipboardTask24Regular,      roles:["Admin","Super Admin"] },
      { label:"Category Performance",   icon:ClipboardTask24Regular,      roles:["Admin","Super Admin","Coach"] },
      { label:"Performance Report",     icon:BookContacts24Regular,       roles:["Admin","Super Admin","Coach"] },
      { label:"Progress Summary",       icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Coach"] },
      { label:"Performance Change Log", icon:ClipboardTask24Regular,      roles:["Admin","Super Admin","Coach"] },
      { label:"Coach Evaluation",       icon:ClipboardTask24Regular,      roles:["Admin","Super Admin","Parent"] },
      { label:"Dynamic Report",         icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Coach","Staff"] },
      { label:"Lane Analytics",         icon:TaskListSquareLtr24Regular,  roles:["Admin","Super Admin"] },
    ],
  },
  {
    label:"Point of Sale", icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Staff"],
    children:[
      { label:"Register",   icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Staff"] },
      { label:"Catalog",    icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Staff"] },
      { label:"Inventory",  icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Staff"] },
      { label:"Orders",     icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Staff"] },
      // Customers — turned off pending redesign alongside the customer loyalty program.
      { label:"Tax & Discounts",  icon:DocumentBulletList24Regular, roles:["Admin","Super Admin","Staff"] },
      { label:"Reports",    icon:DocumentBulletList24Regular, roles:["Admin","Super Admin"] },
    ],
  },
  {
    label:"System Administration", icon:Wrench24Regular, roles:["Admin","Super Admin"],
    children:[
      { label:"App Settings",    icon:Settings24Regular, roles:["Admin","Super Admin"] },
      { label:"Users",           icon:Person24Regular,   roles:["Admin","Super Admin"] },
      { label:"Roles",           icon:Key24Regular,      roles:["Admin","Super Admin"] },
      { label:"Field Access",    icon:Shield24Regular,   roles:["Admin","Super Admin"] },
      { label:"Facility Config", icon:Settings24Regular, roles:["Admin","Super Admin"] },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const hexToRgb = (hex) => {
  try {
    const b = parseInt((hex||"#034e7b").replace("#",""), 16);
    return `${(b>>16)&255}, ${(b>>8)&255}, ${b&255}`;
  } catch { return "3,78,123"; }
};

// Rotating accent per sidebar group on the welcome screen's quick-access cards —
// purely cosmetic grouping cue, has no bearing on which links actually show (that's sections).
const QUICK_COLOR_PALETTE = [
  "3,78,123", "5,150,105", "79,70,229", "220,38,38",
  "8,145,178", "124,58,237", "234,88,12", "219,39,119",
];

const MONTH_OPTIONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FINANCE_YEARS = ["2025", "2026"];

function filterSections(user) {
  const role  = user.user_role;
  const perms = user.permissions || [];

  // Super Admin and Admin always see everything tagged for their role — permissions config
  // is intended for Coach / Staff / Parent granular control, not to restrict Admin.
  if (user.is_superadmin || role === "Super Admin" || role === "Admin") {
    return ALL_SECTIONS
      .map((s) => {
        const children = s.children.filter(c => !c.roles.length || c.roles.includes(role));
        return children.length ? { ...s, children } : null;
      })
      .filter(Boolean);
  }

  return ALL_SECTIONS
    .map((s) => {
      const children = s.children.filter((c) => {
        // Permissions explicitly configured — honour them for Coach / Staff / Parent
        if (perms.length > 0) return perms.includes(c.label);
        // No permissions stored yet — fall back to hardcoded role list
        return !c.roles.length || c.roles.includes(role);
      });
      return children.length ? { ...s, children } : null;
    })
    .filter(Boolean);
}

// ─── Nav group header ─────────────────────────────────────────────────────────
function GroupHeader({ icon:Icon, label, isOpen, collapsed, onClick, secondaryColor, secondaryRGB, isDark }) {
  const [hov, setHov] = useState(false);
  const baseClr = isDark ? "rgba(255,255,255,0.58)" : "rgba(3,78,123,0.65)";
  const activeClr = isDark ? "rgba(255,255,255,0.92)" : "rgba(3,78,123,0.95)";
  const hovBg = isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.05)";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"flex", alignItems:"center",
        gap: collapsed ? 0 : 9,
        padding: collapsed ? "8px 0" : "7px 10px",
        borderRadius:9, cursor:"pointer",
        justifyContent: collapsed ? "center" : "flex-start",
        background: isOpen ? `rgba(${secondaryRGB},0.12)` : hov ? hovBg : "transparent",
        transition:"background 0.14s",
        marginBottom:1,
      }}>
      <Icon style={{
        color: isOpen ? secondaryColor : baseClr,
        fontSize:18, flexShrink:0, transition:"color 0.14s",
      }} />
      {!collapsed && (
        <>
          <span style={{
            flex:1, fontSize:10, fontWeight:800,
            letterSpacing:"0.09em", textTransform:"uppercase",
            color: isOpen ? activeClr : baseClr,
            transition:"color 0.14s",
          }}>{label}</span>
          <ChevronRight24Regular style={{
            color: isDark ? "rgba(255,255,255,0.28)" : "rgba(3,78,123,0.25)", fontSize:14,
            transform: isOpen ? "rotate(90deg)" : "none",
            transition:"transform 0.22s cubic-bezier(0.4,0,0.2,1)",
          }} />
        </>
      )}
    </div>
  );
}

// ─── Nav item ─────────────────────────────────────────────────────────────────
function NavItem({ icon:Icon, label, isActive, collapsed, onClick, secondaryColor, primaryColor, secondaryRGB, indent, isDark }) {
  const [hov, setHov] = useState(false);
  const baseClr = isDark ? "rgba(255,255,255,0.60)" : "rgba(3,78,123,0.70)";
  const activeClr = isDark ? "white" : getBrandColors().blueDark;
  const hovBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.05)";

  if (collapsed) {
    const bg = isActive ? `rgba(${secondaryRGB},0.24)` : hov ? hovBg : "transparent";
    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          width:34, height:34, borderRadius:9, margin:"0 auto 2px",
          display:"flex", alignItems:"center", justifyContent:"center",
          background:bg,
          border: isActive ? `1px solid rgba(${secondaryRGB},0.38)` : "1px solid transparent",
          cursor:"pointer", transition:"all 0.14s",
        }}>
        <Icon style={{
          color: isActive ? secondaryColor : baseClr,
          fontSize:16,
        }} />
      </div>
    );
  }

  const bg = isActive ? `rgba(${secondaryRGB},0.15)` : hov ? hovBg : "transparent";
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"flex", alignItems:"center", gap:9,
        padding:`6px 10px 6px ${indent ? 20 : 10}px`,
        borderRadius:"0 9px 9px 0",
        marginLeft:-8,
        boxShadow: isActive ? `inset 3px 0 0 ${secondaryColor}` : "inset 3px 0 0 transparent",
        background:bg,
        cursor:"pointer", transition:"all 0.14s",
        marginBottom:1,
      }}>
      <Icon style={{
        color: isActive ? secondaryColor : baseClr,
        fontSize:15, flexShrink:0, transition:"color 0.14s",
      }} />
      <span style={{
        fontSize:13, fontWeight:isActive ? 700 : 500,
        color: isActive ? activeClr : baseClr,
        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
      }}>{label}</span>
    </div>
  );
}

// ─── Sign-out row ─────────────────────────────────────────────────────────────
function SignOutRow({ collapsed, isActive, onClick, isDark }) {
  const [hov, setHov] = useState(false);
  const active = hov || isActive;
  const baseClr = isDark ? "rgba(255,255,255,0.52)" : "rgba(3,78,123,0.60)";
  const activeClr = isDark ? "rgba(255,255,255,0.90)" : getBrandColors().blueDark;
  const hovBg = isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.06)";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:"flex", alignItems:"center", gap:9,
        padding: collapsed ? "7px 0" : "7px 10px",
        borderRadius:9, cursor:"pointer",
        justifyContent: collapsed ? "center" : "flex-start",
        background: active ? hovBg : "transparent",
        border: active ? (isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(3,78,123,0.18)") : "1px solid transparent",
        transition:"all 0.14s",
      }}>
      <SignOut24Regular style={{
        color: active ? activeClr : baseClr,
        fontSize:18, transition:"color 0.14s",
      }} />
      {!collapsed && (
        <span style={{
          fontSize:13, fontWeight:600,
          color: active ? activeClr : baseClr,
          transition:"color 0.14s",
        }}>Sign Out</span>
      )}
    </div>
  );
}

// ─── Quick-nav card (welcome screen) ─────────────────────────────────────────
// Shared rectangular glass-pill style for the sidebar's utility rows (theme toggle,
// collapse toggle) — tinted translucent glass, no solid white fill, so it reads
// consistently with the login page's toggle (see LoginPage.js's matching style).
function sidebarUtilBtnStyle(collapsed, isDark, primaryColor) {
  return {
    display:"flex", alignItems:"center",
    justifyContent: collapsed ? "center" : "flex-start",
    gap:8, padding:"7px 12px", borderRadius:10,
    cursor:"pointer", transition:"background 0.15s",
    color: isDark ? "rgba(255,255,255,0.75)" : primaryColor,
    background: isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.05)",
    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(3,78,123,0.10)",
  };
}

function QuickCard({ Icon, label, colorRGB, onClick, primaryColor, isDark }) {
  const [hov, setHov] = useState(false);
  const cardBg   = isDark
    ? (hov ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.06)")
    : (hov ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.78)");
  const cardBdr  = isDark
    ? (hov ? `1.5px solid rgba(${colorRGB},0.40)` : "1.5px solid rgba(255,255,255,0.10)")
    : (hov ? `1.5px solid rgba(${colorRGB},0.22)` : "1.5px solid rgba(255,255,255,0.65)");
  const cardSh   = isDark
    ? (hov ? `0 14px 36px rgba(0,0,0,0.40), 0 0 0 1px rgba(${colorRGB},0.15)` : "0 4px 16px rgba(0,0,0,0.25)")
    : (hov ? `0 14px 36px rgba(0,40,100,0.14), 0 0 0 1px rgba(${colorRGB},0.08)` : "0 4px 16px rgba(0,40,100,0.07)");
  const labelClr = isDark ? "rgba(255,255,255,0.88)" : primaryColor;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding:"16px 18px", borderRadius:18, cursor:"pointer",
        background:cardBg,
        backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
        border:cardBdr, boxShadow:cardSh,
        display:"flex", flexDirection:"row", alignItems:"center", gap:14,
        transform: hov ? "translateY(-4px)" : "none",
        transition:"all 0.18s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
      <div style={{
        width:42, height:42, borderRadius:13, flexShrink:0,
        background:`rgba(${colorRGB},0.12)`,
        border:`1.5px solid rgba(${colorRGB},${isDark ? "0.30" : "0.18"})`,
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow: hov ? `0 4px 12px rgba(${colorRGB},0.22)` : "none",
        transition:"box-shadow 0.18s",
      }}>
        <Icon style={{ color:`rgb(${colorRGB})`, fontSize:22 }} />
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:700, color:labelClr, lineHeight:1.25 }}>{label}</div>
        <div style={{ fontSize:11, color:"#9aabb8", marginTop:2, fontWeight:500 }}>Open →</div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// LandingPage
// ══════════════════════════════════════════════════════════════════════════════
function LandingPage({ currentUser, currentUserId, onLogout }) {
  const { config } = useAppConfig();
  const { isDark, toggleTheme } = useTheme();

  // Inject Outfit font once
  useEffect(() => {
    if (!document.getElementById("lp-outfit-font")) {
      const s = document.createElement("style");
      s.id = "lp-outfit-font";
      s.textContent = OUTFIT_CSS;
      document.head.appendChild(s);
    }
  }, []);

  // Config-driven design tokens
  const primaryColor   = config.primary_color   || Colors.primaryBlue || "#034e7b";
  const secondaryColor = config.secondary_color  || Colors.logoYellow  || "#f7ce0b";
  const sidebarOpacity = parseFloat(config.sidebar_opacity || "0.50");
  const configFont     = `'${config.font_family||"Segoe UI"}', system-ui, sans-serif`;
  const orgName        = config.org_name || "Sixers Cricket Academy";
  const logoSrc        = config.logo_url || SixersLogo;

  // Shell always uses Outfit
  const shellFont = "'Outfit', system-ui, sans-serif";

  const primaryRGB   = hexToRgb(primaryColor);
  const secondaryRGB = hexToRgb(secondaryColor);

  const role     = currentUser?.user_role || "";

  // Airtight Super Admin check — use ALL_SECTIONS directly, never trust filtering
  const isSuperAdmin = !!(currentUser?.is_superadmin || currentUser?.user_role === "Super Admin");
  const sections = isSuperAdmin ? ALL_SECTIONS : filterSections(currentUser);
  const initials = (currentUser?.full_name || "?")
    .split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  const [activePage,    setActivePage]    = useState("");
  const [openGroup,     setOpenGroup]     = useState("");
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [financeYear,   setFinanceYear]   = useState("2026");
  const [financeMonthsSelected, setFinanceMonthsSelected] = useState([...MONTH_OPTIONS]);
  const leftPaneRef = useRef(null);
  const financeMonths = financeMonthsSelected.length ? financeMonthsSelected : [...MONTH_OPTIONS];
  const allMonthsSelected = financeMonthsSelected.length === MONTH_OPTIONS.length;

  const toggleFinanceMonth = (month) => {
    setFinanceMonthsSelected((prev) => {
      if (prev.includes(month)) return prev.filter((m) => m !== month);
      return [...prev, month];
    });
  };

  const selectAllFinanceMonths = () => setFinanceMonthsSelected([...MONTH_OPTIONS]);
  const clearAllFinanceMonths = () => setFinanceMonthsSelected([]);

  const autoSelectSingle = (children) => { if (children.length === 1) setActivePage(children[0].label); };
  const toggleMenu  = () => setMenuCollapsed(p => !p);
  const toggleGroup = (group, children) => {
    if (openGroup === group) setOpenGroup("");
    else { setOpenGroup(group); autoSelectSingle(children); }
  };
  const handleLogout = () => {
    setActivePage("Logout");
    setOpenGroup("");
    setTimeout(() => onLogout && onLogout(), 700);
  };

  // Sidebar geometry
  const SIDE_W    = menuCollapsed ? 56 : 224;
  const sideAlpha = Math.min(sidebarOpacity + 0.30, 0.96);
  const sideBot   = Math.max(sidebarOpacity, 0.36);
  const sideBg    = `linear-gradient(170deg, rgba(${primaryRGB},${sideAlpha}) 0%, rgba(${primaryRGB},${sideBot}) 100%)`;

  const renderContent = () => {
    switch (activePage) {
      // Financials
      case "Academy Dashboard":
      case "Performance Intelligence":     return <ExecutiveDashboard />;
      case "Finance Overview":
      case "Finance Dashboard":
        return <FinancialDashboard selectedYear={financeYear} selectedMonths={financeMonths} isDark={isDark} hideHeader />;
      case "Billing Hub":              return <BillingHub />;
      // Student Management
      case "Client Profile":          return <ClientMaster currentUserId={currentUserId} />;
      case "Student Profiles":
      case "Student Master":          return <ClientMaster currentUserId={currentUserId} />;
      case "Approval Queue":
      case "New Enrolments":
      case "Registration Approval":   return <ApprovalQueue currentUser={currentUser} />;
      // Performance Tracking
      case "Record Performance":
      case "Performance Update":      return <StudentPerformanceForm currentUserId={currentUserId} onNavigate={setActivePage} />;
      case "Upload Performance":      return <PerformanceUpload currentUser={currentUser} />;
      case "Coach Attendance":        return <CoachAttendance currentUser={currentUser} />;
      // Programs & Batches
      case "Coaching Programs":       return <CoachingProgram />;
      case "Batch Management":        return <BatchManagement currentUser={currentUser} />;
      case "Batch Data Import":
      case "Batch Upload":            return <BatchBulkUpload currentUser={currentUser} />;
      case "Kiosk Check-In":          return <KioskCheckIn currentUser={currentUser} />;
      // Coach Planning
      case "Monthly Planner":
      case "Monthly Plan":            return <CoachPlanner currentUser={currentUser} />;
      case "My Schedule":
      case "My Coach Schedule":       return <CoachScheduleView currentUser={currentUser} />;
      case "Clock-In":                return <CoachClockIn currentUser={currentUser} />;
      // 1-on-1 Coaching
      case "Book a Session":
      case "1-on-1 Booking":          return <OTOBooking currentUser={currentUser} />;
      case "My Sessions":
      case "My 1-on-1 Schedule":      return <OTOCoachView currentUser={currentUser} />;
      case "1-on-1 Admin":
      case "Revenue Dashboard":
      case "1-on-1 Dashboard":        return <OTOAdminDashboard />;
      case "1-on-1 Financials":       return <OTOFinancials />;
      case "My Availability":         return <OTOAvailability currentUser={currentUser} />;
      // Reports & Insights
      case "Promotion Readiness":     return <PromotionReadinessReport />;
      case "Coach Compliance":        return <CoachComplianceReport />;
      case "Category Performance":    return <CategoryPerformanceReport />;
      case "Performance Report":      return <CoachPerformanceView mode="dashboard" currentUser={currentUser} />;
      case "Student Roster":          return <StudentRosterReport />;
      case "Attendance Report":       return <AttendanceReport />;
      case "Progress Summary":        return <CoachPerformanceView mode="metrics" currentUser={currentUser} />;
      case "Performance Change Log":  return <PerformanceChangeLog currentUser={currentUser} />;
      case "Coach Evaluation":        return <CoachEvaluation />;
      case "Dynamic Report":          return <DynamicReport />;
      case "Batch Schedule Report":   return <BatchScheduleReport />;
      case "Coach Payment Report":    return <CoachPaymentReport />;

      case "Register":                return <POSRegister currentUser={currentUser} />;
      case "Catalog":                 return <POSCatalog currentUser={currentUser} />;
      case "Inventory":               return <POSInventory currentUser={currentUser} />;
      case "Orders":                  return <POSOrders currentUser={currentUser} />;
      case "Customers":               return <POSCustomers currentUser={currentUser} />;
      case "Tax & Discounts":         return <POSDiscounts currentUser={currentUser} />;
      case "Reports":                 return <POSReports currentUser={currentUser} />;
      // Lane Booking
      case "Lane Booking Portal":   return <LaneBookingPortal currentUser={currentUser} />;
      case "Lane Calendar":         return <LaneBookingCalendar currentUser={currentUser} />;
      case "Booking Management":    return <LaneBookingAdmin />;
      case "Lane Analytics":        return <LaneBookingDashboard />;
      case "Customer Approval":     return <ApprovalQueue currentUser={currentUser} />;
      case "Facility Config":       return <LaneBookingConfig />;
      // Free Trials
      case "Free Trials":           return <FreeTrials />;
      // Tournaments
      case "Tournament Hub": return <TournamentHub />;
      case "Tournament Cost Planner": return <TournamentCostPlanner onNavigate={setActivePage} />;
      case "Tournament Scheduler": return <TournamentScheduler />;
      case "Tournament Expense": return <TournamentExpense />;
      case "Tournament P&L Report": return <TournamentPLReport />;
      case "Tournament Admin Approval": return <TournamentAdminApproval />;
      // System Administration
      case "App Settings":
      case "Config Master":           return <ConfigMaster currentUserId={currentUserId} />;
      case "Parent Portal":           return <ParentDashboard currentUser={currentUser} onLogout={onLogout} />;
      case "Users":                   return <UserMaster currentUserId={currentUserId} />;
      case "Roles":                   return <RoleMaster currentUserId={currentUserId} />;
      case "Field Access":
      case "Fields Authorization":    return <FieldAuthorization currentUserId={currentUserId} />;
      case "Logout":
        return (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%" }}>
            <p style={{ color:"#8899aa", fontSize:15, fontFamily:shellFont }}>Signing out…</p>
          </div>
        );
      default:
        return (
          <div style={{
            display:"flex", flexDirection:"column",
            alignItems:"center", justifyContent:"center",
            minHeight:"100%", padding:"52px 32px 60px",
            fontFamily:shellFont, boxSizing:"border-box",
            /* Dot-grid background */
            backgroundImage:`radial-gradient(circle, rgba(${primaryRGB},0.10) 1.2px, transparent 1.2px)`,
            backgroundSize:"26px 26px",
          }}>
            {/* Logo */}
            <div style={{
              width:96, height:96, marginBottom:28,
              display:"flex", alignItems:"center", justifyContent:"center",
              filter:`drop-shadow(0 12px 32px rgba(${primaryRGB},0.16))`,
            }}>
              <img src={logoSrc} alt={orgName} style={{ width:80, objectFit:"contain" }} />
            </div>

            {/* Academy name */}
            <div style={{
              fontSize:11, fontWeight:800, color:welcomeText,
              letterSpacing:"0.14em", textTransform:"uppercase",
              marginBottom:4, opacity:0.55,
            }}>{orgName}</div>

            {/* Greeting */}
            <h1 style={{
              margin:"0 0 12px", fontSize:30, fontWeight:800,
              color:welcomeText, letterSpacing:"-0.03em", textAlign:"center",
            }}>
              Welcome back,{" "}
              <span style={{ color:secondaryColor }}>
                {currentUser?.full_name?.split(" ")[0] || "User"}
              </span>
            </h1>

            {/* Role badge */}
            <div style={{
              display:"inline-flex", alignItems:"center", gap:8,
              padding:"6px 18px", borderRadius:40, marginBottom:14,
              background:`rgba(${secondaryRGB},0.12)`,
              border:`1.5px solid rgba(${secondaryRGB},0.35)`,
            }}>
              <div style={{
                width:7, height:7, borderRadius:"50%", background:secondaryColor,
                boxShadow:`0 0 8px rgba(${secondaryRGB},0.65)`,
              }} />
              <span style={{
                fontSize:11, fontWeight:800, color:welcomeText,
                letterSpacing:"0.10em", textTransform:"uppercase",
              }}>{role || "User"}</span>
            </div>

            <p style={{
              color:welcomeMuted, fontSize:13, lineHeight:1.7,
              textAlign:"center", maxWidth:380, margin:"0 0 40px",
            }}>
              {config.org_tagline || "Select a section from the sidebar to get started."}
            </p>

            {/* Quick-access — mirrors exactly what's in the left sidebar for this role,
                grouped the same way, so this screen never drifts out of sync with the menu. */}
            <div style={{ width:"100%", maxWidth:860 }}>
              {sections.filter(g => g.children && g.children.length).map((group, gi) => (
                <div key={group.label} style={{ marginBottom:28 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                    <div style={{ height:1, flex:1, background: isDark ? "rgba(255,255,255,0.12)" : `rgba(${primaryRGB},0.12)` }} />
                    <span style={{
                      fontSize:10, fontWeight:700, color: isDark ? "rgba(255,255,255,0.40)" : `rgba(${primaryRGB},0.45)`,
                      letterSpacing:"0.10em", textTransform:"uppercase", whiteSpace:"nowrap",
                    }}>{group.label}</span>
                    <div style={{ height:1, flex:1, background: isDark ? "rgba(255,255,255,0.12)" : `rgba(${primaryRGB},0.12)` }} />
                  </div>
                  <div style={{
                    display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))",
                    gap:16,
                  }}>
                    {group.children.map(child => (
                      <QuickCard
                        key={child.label}
                        Icon={child.icon || group.icon}
                        label={child.label}
                        colorRGB={QUICK_COLOR_PALETTE[gi % QUICK_COLOR_PALETTE.length]}
                        primaryColor={primaryColor}
                        isDark={isDark}
                        onClick={() => setActivePage(child.label)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
    }
  };

  const pageBg = isDark
    ? "linear-gradient(160deg, #020d1a 0%, #040f20 50%, #020c18 100%)"
    : "linear-gradient(160deg, #eef4fc 0%, #f0f6ff 50%, #f5f9fe 100%)";
  const welcomeText = isDark ? "rgba(255,255,255,0.92)" : primaryColor;
  const welcomeMuted = isDark ? "rgba(255,255,255,0.50)" : "#9aabb8";

  return (
    <div style={{ display:"flex", height:"100dvh", position:"relative", overflow:"hidden", fontFamily:shellFont }}>
      <style>{`
        @keyframes lp_haloBlue {
          0%,100% { box-shadow: 0 0 0 4px rgba(3,78,123,0.08), 0 0 14px 4px rgba(3,78,123,0.18); }
          50%     { box-shadow: 0 0 0 7px rgba(3,78,123,0.13), 0 0 28px 8px rgba(3,78,123,0.34); }
        }
        @keyframes lp_haloYellow {
          0%,100% { box-shadow: 0 0 0 4px rgba(247,206,11,0.12), 0 0 14px 4px rgba(247,206,11,0.28); }
          50%     { box-shadow: 0 0 0 7px rgba(247,206,11,0.20), 0 0 28px 8px rgba(247,206,11,0.52); }
        }
      `}</style>
      <FuturisticBackground />

      {/* ═══════════════ SIDEBAR ═══════════════ */}
      <div ref={leftPaneRef} style={{
        width:SIDE_W, flexShrink:0,
        margin: "14px 0 14px 14px",
        borderRadius: 24,
        position:"relative", overflow:"hidden",
        display:"flex", flexDirection:"column",
        boxShadow: isDark ? "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)" : "0 20px 50px rgba(3,78,123,0.15), 0 0 0 1px rgba(255,255,255,0.6)",
        transition:"width 0.28s cubic-bezier(0.4,0,0.2,1)",
        zIndex: 100
      }}>
        {/* Sidebar Background (Image + Glass) */}
        <div style={{
          position: "absolute", inset:0, zIndex:-1,
          backgroundImage: `url(${bg6})`, backgroundSize: "cover", backgroundPosition: "center",
          opacity: isDark ? 0.12 : 0.06
        }} />
        <div style={{
          position: "absolute", inset:0, zIndex:-1,
          background: isDark ? "rgba(2,13,24,0.75)" : "rgba(255,255,255,0.75)",
          backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)"
        }} />

        {/* ─── Logo & org header ─── */}
        <div style={{
          padding: menuCollapsed ? "18px 0 14px" : "20px 14px 16px",
          textAlign:"center",
          borderBottom:"1px solid rgba(255,255,255,0.08)",
          flexShrink:0,
        }}>
          {/* Logo ring */}
          <div style={{
            width: menuCollapsed ? 44 : 82,
            height: menuCollapsed ? 44 : 82,
            borderRadius:"50%",
            border: isDark
              ? `2.5px solid rgba(${secondaryRGB},0.70)`
              : `2.5px solid rgba(${primaryRGB},0.55)`,
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto",
            transition:"width 0.28s, height 0.28s",
            animation: isDark
              ? "lp_haloYellow 2.6s ease-in-out infinite"
              : "lp_haloBlue 2.6s ease-in-out infinite",
          }}>
            <img src={logoSrc} alt={orgName} style={{
              width: menuCollapsed ? 30 : 64,
              objectFit:"contain",
              transition:"width 0.28s",
              opacity:0.95,
            }} />
          </div>

          {/* Text below logo */}
          {!menuCollapsed && (
            <div style={{ marginTop:12 }}>
              <div style={{
                fontSize:11, fontWeight:800,
                color:secondaryColor,
                letterSpacing:"0.10em", textTransform:"uppercase",
                lineHeight:1.3,
              }}>{orgName}</div>
              <div style={{
                fontSize:10, fontWeight:500,
                color: isDark ? "rgba(255,255,255,0.42)" : "rgba(3,78,123,0.50)",
                letterSpacing:"0.06em",
                marginTop:2,
              }}>Management Portal</div>
            </div>
          )}
        </div>

        {/* ─── Navigation (scrollable) ─── */}
        <div style={{
          flex:1, overflowY:"auto", overflowX:"hidden",
          padding: menuCollapsed ? "10px 8px" : "10px 8px 10px 16px",
          scrollbarWidth:"none", msOverflowStyle:"none",
        }}>
          {sections.map((section) => {
            const isOpen = openGroup === section.label;
            return (
              <div key={section.label} style={{ marginBottom:3 }}>
                <GroupHeader
                  icon={section.icon}
                  label={section.label}
                  isOpen={isOpen}
                  collapsed={menuCollapsed}
                  onClick={() => toggleGroup(section.label, section.children)}
                  secondaryColor={secondaryColor}
                  secondaryRGB={secondaryRGB}
                  isDark={isDark}
                />
                {isOpen && (
                  <div style={{ paddingBottom:4 }}>
                    {section.children.map((child) => (
                      <NavItem
                        key={child.label}
                        icon={child.icon}
                        label={child.label}
                        isActive={activePage === child.label}
                        collapsed={menuCollapsed}
                        onClick={() => setActivePage(child.label)}
                        secondaryColor={secondaryColor}
                        primaryColor={primaryColor}
                        secondaryRGB={secondaryRGB}
                        indent={!menuCollapsed}
                        isDark={isDark}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ─── Bottom: collapse toggle + user + appearance + sign out ─── */}
        <div style={{
          padding: menuCollapsed ? "8px 8px" : "8px 10px",
          borderTop:"1px solid rgba(255,255,255,0.08)",
          flexShrink:0,
          display:"flex", flexDirection:"column", gap:5,
        }}>
          {/* Theme toggle — shares SIDEBAR_UTIL_BTN with the collapse toggle, and matches the login page's toggle */}
          <div
            onClick={toggleTheme}
            title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
            style={sidebarUtilBtnStyle(menuCollapsed, isDark, primaryColor)}
            onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.10)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.05)"; }}
          >
            {isDark ? <WeatherSunny24Regular fontSize={16} /> : <WeatherMoon24Regular fontSize={16} />}
            {!menuCollapsed && <span style={{ fontSize:12, fontWeight:700 }}>{isDark ? "Light Mode" : "Dark Mode"}</span>}
          </div>

          {/* Collapse/expand toggle — sits directly above the user chip */}
          <div
            onClick={toggleMenu}
            title={menuCollapsed ? "Expand menu" : "Collapse menu"}
            style={sidebarUtilBtnStyle(menuCollapsed, isDark, primaryColor)}
            onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.10)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.05)" : "rgba(3,78,123,0.05)"; }}
          >
            {menuCollapsed ? <PanelLeftExpand24Regular fontSize={16} /> : <PanelLeftContract24Regular fontSize={16} />}
            {!menuCollapsed && <span style={{ fontSize:12, fontWeight:700 }}>Collapse menu</span>}
          </div>

          {/* User chip */}
          {!menuCollapsed ? (
            <div style={{
              display:"flex", alignItems:"center", gap:10,
              padding:"7px 12px", borderRadius:10,
              background: isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)",
              border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(3,78,123,0.15)",
            }}>
              <div style={{
                width:30, height:30, borderRadius:"50%", flexShrink:0,
                background:`linear-gradient(135deg, rgba(${secondaryRGB},0.95), rgba(${secondaryRGB},0.55))`,
                border:`2px solid rgba(${secondaryRGB},0.35)`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:11, fontWeight:900, color:primaryColor,
                boxShadow:`0 2px 10px rgba(${secondaryRGB},0.30)`,
              }}>{initials}</div>
              <div style={{ flex:1, overflow:"hidden" }}>
                <div style={{
                  fontSize:12.5, fontWeight:700,
                  color: isDark ? "white" : getBrandColors().blueDark,
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                  letterSpacing:"-0.01em",
                }}>{currentUser?.full_name || "User"}</div>
                <div style={{
                  fontSize:9.5, color:secondaryColor, fontWeight:700,
                  letterSpacing:"0.06em", textTransform:"uppercase", marginTop:1,
                }}>{role}</div>
              </div>
            </div>
          ) : (
            <div style={{
              width:30, height:30, borderRadius:"50%",
              background:`linear-gradient(135deg, rgba(${secondaryRGB},0.95), rgba(${secondaryRGB},0.55))`,
              border:`2px solid rgba(${secondaryRGB},0.35)`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, fontWeight:900, color:primaryColor,
              boxShadow:`0 2px 10px rgba(${secondaryRGB},0.30)`,
              margin:"0 auto",
            }}>{initials}</div>
          )}

          {/* Sign Out — always last */}
          <SignOutRow
            collapsed={menuCollapsed}
            isActive={activePage === "Logout"}
            onClick={onLogout}
            isDark={isDark}
          />
        </div>
      </div>

        {/* ═══════════════ CONTENT ═══════════════ */}
      <div style={{ flex:1, overflowY:"auto" }}>
        {activePage === "Finance Dashboard" && (
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              padding: "12px 18px",
              background: isDark ? "rgba(2,13,26,0.86)" : "rgba(248,251,255,0.92)",
              backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              borderBottom: isDark ? "1px solid rgba(247,206,11,0.15)" : "1px solid rgba(3,78,123,0.12)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: "42px", height: "42px", borderRadius: "12px", flexShrink: 0,
                background: `linear-gradient(135deg, ${secondaryColor}, #fff3a0)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 14px rgba(255,200,0,0.32)",
              }}>
                <FiDollarSign size={20} color={primaryColor} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: isDark ? secondaryColor : primaryColor, letterSpacing: "0.01em" }}>
                  Finance Dashboard
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.72)" : "rgba(3,78,123,0.72)" }}>
                  Select one or more months and a year
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 560px" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.75)" : "rgba(3,78,123,0.75)", whiteSpace: "nowrap" }}>
                  Months
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    paddingBottom: 2,
                    flex: 1,
                    scrollbarWidth: "thin",
                  }}
                >
                  <button
                    type="button"
                    onClick={selectAllFinanceMonths}
                    style={{
                      border: allMonthsSelected
                        ? (isDark ? "1px solid rgba(247,206,11,0.75)" : "1px solid rgba(3,78,123,0.65)")
                        : (isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)"),
                      background: allMonthsSelected
                        ? (isDark ? "rgba(247,206,11,0.22)" : "rgba(3,78,123,0.14)")
                        : (isDark ? "rgba(255,255,255,0.06)" : "white"),
                      color: isDark ? "#f1f5f9" : "#334155",
                      borderRadius: 999,
                      padding: "5px 10px",
                      fontSize: 11,
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={clearAllFinanceMonths}
                    style={{
                      border: isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)",
                      background: isDark ? "rgba(255,255,255,0.06)" : "white",
                      color: isDark ? "#f1f5f9" : "#334155",
                      borderRadius: 999,
                      padding: "5px 10px",
                      fontSize: 11,
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Clear All
                  </button>
                  {MONTH_OPTIONS.map((m) => {
                    const selected = financeMonthsSelected.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleFinanceMonth(m)}
                        style={{
                          border: selected
                            ? (isDark ? "1px solid rgba(247,206,11,0.75)" : "1px solid rgba(3,78,123,0.65)")
                            : (isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)"),
                          background: selected
                            ? (isDark ? "rgba(247,206,11,0.22)" : "rgba(3,78,123,0.14)")
                            : (isDark ? "rgba(255,255,255,0.06)" : "white"),
                          color: isDark ? "#f1f5f9" : "#334155",
                          borderRadius: 999,
                          padding: "5px 10px",
                          fontSize: 11,
                          fontWeight: 800,
                          cursor: "pointer",
                        }}
                      >
                        {selected ? "✓ " : ""}
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.75)" : "rgba(3,78,123,0.75)" }}>
                Year{" "}
                <select
                  value={financeYear}
                  onChange={(e) => setFinanceYear(e.target.value)}
                  style={{
                    marginLeft: 6,
                    minWidth: 92,
                    background: isDark ? "rgba(255,255,255,0.08)" : "white",
                    border: isDark ? "1px solid rgba(247,206,11,0.35)" : "1px solid rgba(3,78,123,0.20)",
                    color: isDark ? "white" : "#334155",
                    padding: "6px 8px",
                    borderRadius: 8,
                    fontWeight: 700,
                  }}
                >
                  {FINANCE_YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
}

export default function LandingPageRoot(props) {
  return (
    <ThemeProvider>
      <LandingPage {...props} />
    </ThemeProvider>
  );
}
